/** Per-entry detail + thread hooks. */
import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ContentDetail,
  MessageAnchor,
  MessageView,
  ScriptDetail,
  ScriptVersionListing,
  ThreadMessagesPage,
} from '@discovr/types';

import { createCampaignDetailApi } from '../api/campaignQueries';
import type { ThreadType } from '../api/shared';
import { campaignKeys } from '../queryKeys';
import {
  POLL_WINDOW_MS,
} from '../constants';
import { patchRosterEntry, bumpRosterEntryToTop, TAB_FOR_THREAD } from './rosterCacheOps';

const api = createCampaignDetailApi();

/** Mounting again inside this window reuses the cached anchors instead of refetching them. */
const ANCHORS_STALE_MS = 30_000;

/** GET /api/v1/roster/{entry_id}/script — full script body + AI envelope. */
export function useScriptDetail(entryId: string, version = 0) {
  return useQuery<ScriptDetail>({
    queryKey: [...campaignKeys.scriptDetail(entryId), version],
    queryFn: () => api.getScriptDetail(entryId),
    enabled: !!entryId,
    staleTime: 30_000,
  });
}

/**
 * GET /api/v1/roster/{entry_id}/content — full content URL + AI envelope.
 * Same `version` cache-bust pattern as useScriptDetail.
 */
/** `pollMs` polls while a GEO finalize is pending; 0 disables polling. */
export function useContentDetail(entryId: string, version = 0, pollMs = 0) {
  return useQuery<ContentDetail>({
    queryKey: [...campaignKeys.contentDetail(entryId), version],
    queryFn: () => api.getContentDetail(entryId),
    enabled: !!entryId,
    staleTime: pollMs > 0 ? 0 : 30_000,
    refetchInterval: pollMs > 0 ? pollMs : false,
  });
}

export function useContentVideoUrl(entryId: string, enabled: boolean) {
  return useQuery<{ url: string }>({
    queryKey: campaignKeys.contentVideoUrl(entryId),
    queryFn: () => api.getContentVideoUrl(entryId),
    enabled: !!entryId && enabled,
    // Presigned URLs expire after 1 hour — treat as stale after 45 min so
    // the player always gets a fresh URL with >15 min of validity remaining.
    staleTime: 45 * 60_000,
    gcTime: 60 * 60_000,  // evict from cache after 1 hour
  });
}

/** GET /api/v1/roster/{entry_id}/threads/{type}/messages — cursor-paginated thread messages. */
export function useRosterThread(entryId: string, type: ThreadType) {
  return useQuery<ThreadMessagesPage>({
    queryKey: campaignKeys.conversation(entryId, type),
    queryFn: () => api.getRosterThread(entryId, type),
    enabled: !!entryId,
    staleTime: 0,
  });
}

/**
 * GET /api/v1/roster/{entry_id}/threads/{type}/anchors — the FULL,
 * unpaginated set of anchored messages for one script version. Backs the
 * Script tab's persistent highlights, independent of useRosterThread's
 * chat-pagination window (a thread with more than one page of chat would
 * otherwise silently lose highlights for anchors older than the last page).
 * Auto-invalidates alongside useRosterThread on a new message send — same
 * ['conversation', entryId, type] key prefix.
 */
export function useAnchoredMessages(
  entryId: string,
  type: ThreadType,
  versionId: string,
  options?: { enabled?: boolean },
) {
  return useQuery<MessageView[]>({
    queryKey: campaignKeys.anchoredMessages(entryId, type, versionId),
    queryFn: () => api.getAnchoredMessages(entryId, type, versionId),
    enabled: !!entryId && !!versionId && (options?.enabled ?? true),
    // Send, edit, delete and the SSE event all invalidate this key, which refetches whatever the staleTime.
    staleTime: ANCHORS_STALE_MS,
  });
}

/** POST /api/v1/roster/{entry_id}/threads/{type}/messages — sends a new message. */
export function usePostRosterMessage(campaignId?: string) {
  const queryClient = useQueryClient();
  return useMutation<
    MessageView,
    Error,
    { entryId: string; type: ThreadType; body: string; anchor?: MessageAnchor; replyTo?: string }
  >({
    mutationFn: ({ entryId, type, body, anchor, replyTo }) =>
      api.postRosterMessage(entryId, type, body, anchor, replyTo),
    onSuccess: (_msg, { entryId, type }) => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.conversation(entryId, type) });
      // Scope strictly to this campaign's roster lists. We intentionally do NOT
      // fall back to a broad ['roster'] prefix when campaignId is absent — that
      // prefix also matches per-entry detail/analytics queries (scriptDetail,
      // contentVideoUrl, analytics polling). Callers in campaign-detail always
      // have a campaignId, so the guard is just defensive.
      if (campaignId) {
        // The BE excludes the sender from the new_message SSE, so float our own
        // row to the top optimistically here, scoped to this thread's tab; the
        // invalidate then reconciles the authoritative recency order.
        bumpRosterEntryToTop(queryClient, campaignId, entryId, TAB_FOR_THREAD[type]);
        queryClient.invalidateQueries({ queryKey: campaignKeys.rosterAll(campaignId) });
      }
    },
  });
}

/** PATCH /api/v1/roster/{entry_id}/threads/{type}/messages/{message_id} — edits an own message. */
export function useEditRosterMessage(campaignId?: string) {
  const queryClient = useQueryClient();
  return useMutation<
    MessageView,
    Error,
    { entryId: string; type: ThreadType; messageId: string; body: string }
  >({
    mutationFn: ({ entryId, type, messageId, body }) =>
      api.editRosterMessage(entryId, type, messageId, body),
    onSuccess: (_msg, { entryId, type }) => {
      // Invalidating the conversation prefix also refreshes the nested
      // anchored-messages query (['conversation', entryId, type, 'anchors', …]),
      // so an edited/removed script comment's highlight updates too.
      queryClient.invalidateQueries({ queryKey: campaignKeys.conversation(entryId, type) });
      // Editing the latest message changes the roster row's "Last Comment"
      // preview, so refresh the roster lists too (same reason usePostRosterMessage does).
      if (campaignId) {
        queryClient.invalidateQueries({ queryKey: campaignKeys.rosterAll(campaignId) });
      }
    },
  });
}

/** DELETE /api/v1/roster/{entry_id}/threads/{type}/messages/{message_id} — removes a message (author or admin). */
export function useDeleteRosterMessage(campaignId?: string) {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    { entryId: string; type: ThreadType; messageId: string }
  >({
    mutationFn: ({ entryId, type, messageId }) =>
      api.deleteRosterMessage(entryId, type, messageId),
    onSuccess: (_result, { entryId, type }) => {
      // Conversation prefix covers the nested anchored-messages query too.
      queryClient.invalidateQueries({ queryKey: campaignKeys.conversation(entryId, type) });
      // Deleting the latest message changes the roster row's "Last Comment"
      // preview (and reconciles a stale unread count), so refresh the lists.
      if (campaignId) {
        queryClient.invalidateQueries({ queryKey: campaignKeys.rosterAll(campaignId) });
      }
    },
  });
}

/** POST /api/v1/roster/{entry_id}/threads/{type}/read — clears the unread badge for the calling user. */
export function useMarkThreadRead(campaignId?: string) {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    { entryId: string; type: ThreadType; lastReadMessageId?: string }
  >({
    mutationFn: ({ entryId, type, lastReadMessageId }) =>
      api.markRosterThreadRead(entryId, type, lastReadMessageId),
    onSuccess: (_result, { entryId, type }) => {
      if (!campaignId) return;
      // Scope to this thread's tab so a script/content mark-read doesn't zero the
      // Creators-tab selection badge for the same entry (each tab's unread_count
      // is a different thread's count).
      patchRosterEntry(queryClient, campaignId, entryId, (entry) => ({ ...entry, unread_count: 0 }), TAB_FOR_THREAD[type]);
    },
  });
}

const SEEN_FIELD_FOR_PHASE = { script: 'script_seen', content: 'content_seen' } as const;
const TAB_FOR_PHASE = { script: 'scripts', content: 'content' } as const;

/** POST /api/v1/roster/{entry_id}/{phase}/seen — clears the floating "New" badge for the calling user. */
export function useMarkPostSeen(campaignId?: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { entryId: string; phase: 'script' | 'content' }>({
    mutationFn: ({ entryId, phase }) => api.markPostSeen(entryId, phase),
    onSuccess: (_result, { entryId, phase }) => {
      if (!campaignId) return;
      const field = SEEN_FIELD_FOR_PHASE[phase];
      patchRosterEntry(
        queryClient,
        campaignId,
        entryId,
        (entry) => ({ ...entry, [field]: true }),
        TAB_FOR_PHASE[phase],
      );
    },
  });
}

/**
 * GET /api/v1/roster/{entry_id}/script/versions — the archive listing.
 * Enabled only while the review panel is open for the entry.
 */
export function useScriptVersions(entryId: string, enabled = true) {
  return useQuery<ScriptVersionListing>({
    queryKey: [...campaignKeys.scriptDetail(entryId), 'versions'],
    queryFn: () => api.getScriptVersions(entryId),
    enabled: !!entryId && enabled,
    staleTime: 30_000,
  });
}

/** GET /api/v1/roster/{entry_id}/analytics/metrics InsightIQ / Phyllo analytics for the creator insights drawer. */
export function useCreatorAnalyticsMetrics(entryId: string, enabled = true) {
  const pollStartRef = useRef<number | null>(null);

  return useQuery<{ status: string; data?: Record<string, unknown> } | null>({
    queryKey: ['roster', 'analytics', 'metrics', entryId],
    queryFn: () => api.getCreatorAnalytics(entryId),
    enabled: !!entryId && enabled,
    staleTime: 5 * 60_000,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      if (s === 'done') {
        pollStartRef.current = null;
        return false;
      }
      pollStartRef.current ??= Date.now();
      if (Date.now() - pollStartRef.current > POLL_WINDOW_MS) return false;
      return 5_000;
    },
  });
}

/** GET /api/v1/roster/{entry_id}/analytics/safety Cached brand-safety result for the creator insights drawer. */
export function useCreatorBrandSafety(entryId: string) {
  return useQuery<{ task_id?: string; status: string; result?: Record<string, unknown> } | null>({
    queryKey: ['roster', 'analytics', 'safety', entryId],
    queryFn: () => api.getCreatorBrandSafety(entryId),
    enabled: !!entryId,
    staleTime: 5 * 60_000,
  });
}

/** Shared so the SSE invalidation cannot drift from the query. */
export function useRosterSentiment(entryId: string) {
  return useQuery({
    queryKey: ['roster', 'sentiment', entryId],
    queryFn: () => api.getRosterSentiment(entryId),
    enabled: !!entryId,
    staleTime: 5 * 60_000,
  });
}
